import type { SingleAddress, MilitaryBase } from '@zoocasa/go-search';
import type { ParsedAddressPath } from 'data/addresses/go-search/addressPathParser';
import type { Filter } from 'contexts/preferences/listing-params/types';

/** The subset of {@link Filter} that identifies *which area* a search is for. */
export type AreaLocationFilter = Pick<Filter, 'slug' | 'areaName'>;

type AreaLocationFilterArgs = {
  requestedAddress?: ParsedAddressPath;
  electedAddress?: SingleAddress;
  militaryBase?: MilitaryBase;
};

/**
 * Builds the `slug` / `areaName` pair identifying the area an area page is displaying.
 *
 * Area pages initialize `listingParams.filter` by merging the URL's SEO filters over
 * `DEFAULT_LISTING_PARAMS_FILTER_SHARED`, which carries `slug: ''` and `areaName: ''` —
 * the URL filters supply status/home type/price only, never the location. That left
 * `listingParams` with no area at all, so everything downstream that persists it recorded
 * the wrong place:
 *
 * - `captureLastSearch` on filter/sort change, surfaced as "My Last Search"
 * - the `listing-params` cookie, re-serialized by every `setFilter`
 * - saved-search creation and map-view navigation
 *
 * A blank `areaName` is then backfilled with the hardcoded `'Toronto, ON'` default in
 * `getFilterParamsFromParsedUrlQuery`, and a missing cookie falls back to the country
 * default (Toronto, ON for CA / Tampa, FL for US) — which is how a search made on, say,
 * Hamilton's area page came back as Toronto.
 *
 * Reaching an area page through the search bar already sets both fields inside
 * `SearchPrediction.transitionToPath`; landing on the URL directly has to match it.
 *
 * The label has to match the format `SearchPrediction.transitionToPath` writes when the same
 * area is reached through the search bar, because `parseNeighbourhoodLocation` and
 * `generateSlugAndCoordinatesFromLastSearch` parse it back by position:
 *
 * - neighbourhood → `Downtown, Toronto, ON` (all three parts; the city cannot be dropped,
 *   or the round trip reads "Downtown" as the city and derives the slug `downtown-on`)
 * - city          → `Hamilton, ON`
 * - province/state and country → the name alone (`Ontario`, `Canada`)
 *
 * `requestedAddress` wins over `electedAddress` so a fallback area page (requested city has
 * no listings, province elected) still records the city the URL names.
 *
 * `boundary` is deliberately not part of this: an area-page search is scoped by `slug`, and
 * the page's own polygon is WKT fetched asynchronously for map drawing only. Area pages null
 * the boundary out on purpose — see the explicit `boundary: null` in the hybrid page's
 * save-search flow — so a stale boundary from an earlier map search cannot narrow the area.
 */
export function getAreaLocationFilter({
  requestedAddress,
  electedAddress,
  militaryBase,
}: AreaLocationFilterArgs): AreaLocationFilter {
  if (militaryBase) {
    return { slug: militaryBase.slug, areaName: militaryBase.displayName };
  }

  // provinceOrState on electedAddress is a lowercase code (e.g. "on"), so upper-case it.
  const provinceOrStateAbbr = electedAddress?.provinceOrState?.toUpperCase();
  const neighbourhood = requestedAddress?.neighbourhood?.name;
  const city = requestedAddress?.subDivision?.name;

  const areaName = neighbourhood || city
    ? [neighbourhood, city, provinceOrStateAbbr].filter(Boolean).join(', ')
    : (requestedAddress?.provinceOrState?.name || requestedAddress?.country?.name || electedAddress?.label);

  return {
    slug: requestedAddress?.slug || electedAddress?.slug || '',
    areaName: areaName || '',
  };
}
